// "Retail & Tea Room Analysis" workbook — the Executive-tab download.
//
// Reproduces Chris's hand-built "By Hour" model as a live-formula workbook,
// sourced from /api/dashboards/exec/retail-tearoom-analysis. Faithful to the
// source layout, sheet for sheet:
//
//   Retail / Tea Rooms            the by-hour grid per outlet + the per-hour
//                                 Hourly Breakdown, validated cell-for-cell.
//   Costs of Goods                management-accounts sales by outlet + the
//                                 month's cost total split by that sales share.
//   Retail Labour / Catering Labour   the same, for payroll.
//   Retail/Tea Rooms with Forecast    the grids again, with untraded/incomplete
//                                 months' totals forecast from recent months.
//   Management Accounts P&L       MA revenue vs the modelled till, with variance.
//   {comparison season}           a full second set (grids + Costs of Goods +
//                                 the two Labour sheets) when one is selected.
//
// Inputs are written as values; everything derived is a real Excel formula.
// exceljs is dynamically imported on use.
import type ExcelJS from 'exceljs'

// ---------------------------------------------------------------------------
//  API payload types
// ---------------------------------------------------------------------------
export interface RtHourly { category: string; outlet: string; month: number; bucket: string; net: number; txn: number; cost: number; costed_net: number }
export interface RtDays { category: string; outlet: string; month: number; days: number }
export interface RtMa { category: string; outlet: string; month: number; net: number }
export interface RtCogs { category: string; month: number; amount: number }
export interface RtLabour { category: string; month: number; employer_cost: number }
export interface RtSeason {
  fiscalYear: number
  fiscalYearLabel: string
  hourly: RtHourly[]
  trading_days: RtDays[]
  ma_revenue: RtMa[]
  cogs: RtCogs[]
  labour: RtLabour[] | null
}
export interface RtPayload {
  fiscalYear: number
  fiscalYearLabel: string
  prevFiscalYear: number
  prevFiscalYearLabel: string
  years: number[]
  labour_available: boolean
  model: { utilities_pct: number }
  retail_outlets: { key: string; group: string; has_hourly: boolean }[]
  tearoom_outlets: { key: string; group: string; has_hourly: boolean }[]
  seasons: Record<string, RtSeason>
}
export interface RtOptions {
  includeOnline: boolean
  includeWhitby: boolean
  /** fiscal year to compare against, or null for none. */
  comparisonFy: number | null
  /** 'spreadsheet' = the fixed per-outlet COGS % from the source sheet; 'actual' = the season's real COGS %. */
  cogsMode: 'spreadsheet' | 'actual'
}

// ---------------------------------------------------------------------------
//  constants
// ---------------------------------------------------------------------------
const MONTHS = ['March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December', 'January', 'February']
const BUCKETS = ['Early', '09:30', '10:30', '11:30', '12:30', '13:30', '14:30', '15:30', '16:30', 'Late']
// £ columns for the ten slots, and the eight (Early–15:30) the averages span.
const SLOT_COLS = [2, 4, 6, 8, 10, 12, 14, 16, 18, 20]
const AVG_COLS = [2, 4, 6, 8, 10, 12, 14, 16]
const LABOUR_RATE = 13.45 * 1.3 // £17.485/hr — Chris's on-costed hourly rate

// The per-outlet assumptions from the source workbook: fixed COGS % (used by the
// "spreadsheet" COGS mode) and hours open per day (utilities divisor). Online /
// Whitby are not in the source, so they carry sensible retail defaults.
const OUTLET_CONST: Record<string, { cogs: number; hours: number; staff: number }> = {
  'Retail|Pickering': { cogs: 47, hours: 7.5, staff: 1 },
  'Retail|Goathland': { cogs: 15, hours: 7.25, staff: 1 },
  'Retail|Grosmont': { cogs: 17, hours: 8, staff: 1 },
  'Retail|Shed Shop': { cogs: 21, hours: 7, staff: 1 },
  'Retail|Online': { cogs: 21, hours: 7.5, staff: 1 },
  'Retail|Whitby': { cogs: 21, hours: 7.5, staff: 1 },
  'Tea Room|Pickering': { cogs: 34, hours: 7.25, staff: 1 },
  'Tea Room|Levisham': { cogs: 1, hours: 7.5, staff: 1 },
  'Tea Room|Goathland': { cogs: 23, hours: 8, staff: 1 },
  'Tea Room|Grosmont': { cogs: 25, hours: 7, staff: 1 },
  'Tea Room|Trolley': { cogs: 17, hours: 7, staff: 1 },
}
const outletConst = (category: string, outlet: string) =>
  OUTLET_CONST[`${category}|${outlet}`] ?? { cogs: 21, hours: 7.5, staff: 1 }

const GREEN = 'FF0B3D2E'
const GREEN2 = 'FF2E7D32'
const SUBHEAD = 'FFEDE7D8'
const TOTAL_BG = 'FFF0F4F1'
const FCAST_BG = 'FFFFF6E0'
const BORDER = 'FFD8D8D8'

const MONEY = '#,##0;-#,##0;"–"'
const MONEY2 = '£#,##0;-£#,##0;"–"'
const INT = '#,##0;-#,##0;"–"'
const PCT = '0.0%;-0.0%;"–"'

type Cell = ExcelJS.Cell

const thin = (argb = BORDER) => ({ style: 'thin' as const, color: { argb } })
function box(cell: Cell) { cell.border = { top: thin(), left: thin(), bottom: thin(), right: thin() } }
function fill(cell: Cell, argb: string) { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } } }
function colLetter(n: number): string {
  let s = ''
  while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26) }
  return s
}
function ref(sheet: string, col: number, row: number, abs = false): string {
  const c = colLetter(col); const a = abs ? '$' : ''
  return `'${sheet.replace(/'/g, "''")}'!${a}${c}${a}${row}`
}

/** Fiscal month m (0 = March) of fy → the UTC date of that month's last day. */
function monthEnd(fy: number, m: number): Date {
  const calYear = m <= 9 ? fy - 1 : fy
  const calMonthIdx = m <= 9 ? m + 2 : m - 10 // Mar=2 … Dec=11, Jan=0, Feb=1
  return new Date(Date.UTC(calYear, calMonthIdx + 1, 0))
}
const isComplete = (fy: number, m: number) => monthEnd(fy, m).getTime() < Date.now()

function indexSeason(s: RtSeason) {
  const hourly = new Map<string, { net: number; txn: number }>()
  for (const h of s.hourly) {
    const k = `${h.category}|${h.outlet}|${h.month}|${h.bucket}`
    const cur = hourly.get(k) ?? { net: 0, txn: 0 }
    cur.net += h.net; cur.txn += h.txn
    hourly.set(k, cur)
  }
  const days = new Map<string, number>()
  for (const d of s.trading_days) days.set(`${d.category}|${d.outlet}|${d.month}`, (days.get(`${d.category}|${d.outlet}|${d.month}`) ?? 0) + d.days)
  const ma = new Map<string, number>()
  for (const m of s.ma_revenue) ma.set(`${m.category}|${m.outlet}|${m.month}`, (ma.get(`${m.category}|${m.outlet}|${m.month}`) ?? 0) + m.net)
  const cogs = new Map<string, number>()
  for (const c of s.cogs) cogs.set(`${c.category}|${c.month}`, (cogs.get(`${c.category}|${c.month}`) ?? 0) + c.amount)
  const labour = new Map<string, number>()
  if (s.labour) for (const l of s.labour) labour.set(`${l.category}|${l.month}`, (labour.get(`${l.category}|${l.month}`) ?? 0) + l.employer_cost)
  return { fy: s.fiscalYear, hourly, days, ma, cogs, labour, hasLabour: s.labour !== null }
}
type SeasonIdx = ReturnType<typeof indexSeason>

function totalNetOf(idx: SeasonIdx, category: string, outlet: string, m: number): number {
  let t = 0
  for (const b of BUCKETS) t += idx.hourly.get(`${category}|${outlet}|${m}|${b}`)?.net ?? 0
  return t
}

// The cost base + financial-sheet rows always carry Online (its cost is in the
// P&L total); Whitby only when its pill is on. The grid blocks carry Online /
// Whitby only when their pill is on.
function baseOutlets(payload: RtPayload, category: 'Retail' | 'Tea Room', opts: RtOptions): string[] {
  const list = category === 'Retail' ? payload.retail_outlets : payload.tearoom_outlets
  return list.filter((o) => o.group === 'core' || o.group === 'online' || (o.group === 'whitby' && opts.includeWhitby)).map((o) => o.key)
}
function gridOutlets(payload: RtPayload, category: 'Retail' | 'Tea Room', opts: RtOptions): string[] {
  const list = category === 'Retail' ? payload.retail_outlets : payload.tearoom_outlets
  return list.filter((o) => o.group === 'core' || (o.group === 'online' && opts.includeOnline) || (o.group === 'whitby' && opts.includeWhitby)).map((o) => o.key)
}

// ---------------------------------------------------------------------------
//  Costs of Goods / Labour — the sales-% split. `only` limits to one category
//  (the two Labour sheets); omit for the combined Costs of Goods sheet.
// ---------------------------------------------------------------------------
interface SplitRefs { cell: (category: string, outlet: string, month: number) => string | null }

function buildSplitSheet(
  wb: ExcelJS.Workbook, sheetName: string, kind: 'COGS' | 'Labour',
  idx: SeasonIdx, payload: RtPayload, opts: RtOptions, label: string,
  only?: 'Retail' | 'Tea Room',
): SplitRefs {
  const ws = wb.addWorksheet(sheetName, { views: [{ state: 'frozen', xSplit: 1, ySplit: 0 }] })
  ws.getColumn(1).width = 22
  for (let c = 2; c <= 14; c++) ws.getColumn(c).width = 11
  const t = ws.getCell(1, 1); t.value = label
  ws.mergeCells(1, 1, 1, 14); t.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } }; fill(t, GREEN); t.alignment = { vertical: 'middle' }
  ws.getRow(1).height = 22
  let row = 3
  const allocCell: Record<string, string> = {}
  const cats: ('Retail' | 'Tea Room')[] = only ? [only] : ['Retail', 'Tea Room']

  for (const category of cats) {
    const outlets = baseOutlets(payload, category, opts)
    if (!outlets.length) continue
    const catLabel = category === 'Retail' ? 'RETAIL' : 'CATERING'

    // Section A: MA revenue by outlet (the % base)
    const secA = ws.getCell(row, 1); secA.value = `${catLabel} — sales (management accounts), used for the % split`
    ws.mergeCells(row, 1, row, 14); secA.font = { bold: true, color: { argb: GREEN } }; fill(secA, SUBHEAD); row++
    const hdr = ws.getRow(row)
    hdr.getCell(1).value = 'Outlet'
    MONTHS.forEach((m, i) => { hdr.getCell(2 + i).value = m.slice(0, 3) })
    hdr.getCell(14).value = 'Total'
    hdr.eachCell((c) => { c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; fill(c, GREEN2); c.alignment = { horizontal: 'right' }; box(c) })
    hdr.getCell(1).alignment = { horizontal: 'left' }; row++
    const revFirst = row
    const revRowOf: Record<string, number> = {}
    for (const outlet of outlets) {
      const r = ws.getRow(row); r.getCell(1).value = outlet
      for (let m = 0; m < 12; m++) { const v = idx.ma.get(`${category}|${outlet}|${m}`) ?? 0; const cell = r.getCell(2 + m); cell.value = Math.round(v); cell.numFmt = MONEY; box(cell) }
      const tc = r.getCell(14); tc.value = { formula: `SUM(B${row}:M${row})` }; tc.numFmt = MONEY; tc.font = { bold: true }; box(tc); box(r.getCell(1))
      revRowOf[outlet] = row; row++
    }
    const revTotalRow = row
    const rt = ws.getRow(row); rt.getCell(1).value = 'Total'
    for (let m = 0; m < 12; m++) { const col = 2 + m; const c = rt.getCell(col); c.value = { formula: `SUM(${colLetter(col)}${revFirst}:${colLetter(col)}${revFirst + outlets.length - 1})` }; c.numFmt = MONEY; c.font = { bold: true }; fill(c, TOTAL_BG); box(c) }
    { const c = rt.getCell(14); c.value = { formula: `SUM(N${revFirst}:N${revFirst + outlets.length - 1})` }; c.numFmt = MONEY; c.font = { bold: true }; fill(c, TOTAL_BG); box(c) }
    rt.getCell(1).font = { bold: true }; fill(rt.getCell(1), TOTAL_BG); box(rt.getCell(1)); row += 2

    // Section B: the month total, split by sales share
    const src = kind === 'COGS' ? idx.cogs : idx.labour
    const secB = ws.getCell(row, 1)
    secB.value = kind === 'COGS' ? `${catLabel} — cost of goods total (P&L), split by sales %` : `${catLabel} — labour total (payroll), split by sales %`
    ws.mergeCells(row, 1, row, 14); secB.font = { bold: true, color: { argb: GREEN } }; fill(secB, SUBHEAD); row++
    const hdrB = ws.getRow(row)
    hdrB.getCell(1).value = kind === 'COGS' ? 'Cost of goods' : 'Labour'
    MONTHS.forEach((m, i) => { hdrB.getCell(2 + i).value = m.slice(0, 3) }); hdrB.getCell(14).value = 'Total'
    hdrB.eachCell((c) => { c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; fill(c, GREEN2); c.alignment = { horizontal: 'right' }; box(c) })
    hdrB.getCell(1).alignment = { horizontal: 'left' }; row++
    const totalRow = row
    const trr = ws.getRow(row); trr.getCell(1).value = 'Month total'
    for (let m = 0; m < 12; m++) { const v = src.get(`${category}|${m}`) ?? 0; const c = trr.getCell(2 + m); c.value = Math.round(v); c.numFmt = MONEY; box(c) }
    { const c = trr.getCell(14); c.value = { formula: `SUM(B${row}:M${row})` }; c.numFmt = MONEY; c.font = { bold: true }; box(c) }
    trr.getCell(1).font = { bold: true, italic: true }; box(trr.getCell(1)); row++
    for (const outlet of outlets) {
      const r = ws.getRow(row); r.getCell(1).value = outlet
      for (let m = 0; m < 12; m++) {
        const col = 2 + m; const cl = colLetter(col)
        const c = r.getCell(col)
        c.value = { formula: `IF(${cl}${revTotalRow}=0,0,${cl}${totalRow}*${cl}${revRowOf[outlet]}/${cl}${revTotalRow})` }
        c.numFmt = MONEY; box(c)
      }
      { const c = r.getCell(14); c.value = { formula: `SUM(B${row}:M${row})` }; c.numFmt = MONEY; c.font = { bold: true }; box(c) }
      box(r.getCell(1)); allocCell[`${category}|${outlet}`] = `${row}`; row++
    }
    row += 2
  }
  return { cell: (category, outlet, month) => { const r = allocCell[`${category}|${outlet}`]; return r ? ref(sheetName, 2 + month, Number(r), true) : null } }
}

// ---------------------------------------------------------------------------
//  Retail / Tea Rooms — the by-hour grid + Hourly Breakdown.
// ---------------------------------------------------------------------------
interface GridRefs { totalNet: (category: string, outlet: string) => string | null }

function buildGridSheet(
  wb: ExcelJS.Workbook, sheetName: string, category: 'Retail' | 'Tea Room',
  idx: SeasonIdx, payload: RtPayload, opts: RtOptions, seasonLabel: string,
  cogsRefs: SplitRefs | null, labourRefs: SplitRefs | null, utilPct: number,
  forecastMode = false, priorIdx: SeasonIdx | null = null,
): GridRefs {
  const ws = wb.addWorksheet(sheetName, { views: [{ state: 'frozen', xSplit: 1, ySplit: 0 }] })
  const V = 22, W = 23, X = 24, Y = 25, Z = 26, AA = 27, AB = 28, AC = 29, AD = 30
  ws.getColumn(1).width = 15
  for (let c = 2; c <= 21; c++) ws.getColumn(c).width = 8
  for (const c of [V, W, X, Y, Z, AA, AB, AC, AD]) ws.getColumn(c).width = 11

  const t = ws.getCell(1, 1)
  t.value = `${category === 'Retail' ? 'Retail' : 'Tea Rooms'} — by hour · ${seasonLabel}${forecastMode ? ' · with forecast' : ''}`
  ws.mergeCells(1, 1, 1, AD); t.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } }; fill(t, GREEN); t.alignment = { vertical: 'middle' }
  ws.getRow(1).height = 22

  const fy = idx.fy
  const outlets = gridOutlets(payload, category, opts)
  let row = 3
  const totalNetCell: Record<string, { row: number }> = {}

  for (const outlet of outlets) {
    const traded = (m: number) => (idx.days.get(`${category}|${outlet}|${m}`) ?? 0) > 0
    const priorTraded = (m: number) => !!priorIdx && (priorIdx.days.get(`${category}|${outlet}|${m}`) ?? 0) > 0
    // The months whose totals are real (traded and fully in the past) — the base
    // the forecast averages over.
    const completeMonths = Array.from({ length: 12 }, (_, m) => m).filter((m) => traded(m) && isComplete(fy, m))
    // Forecast run-rate: this season's complete months vs last season's same
    // months. Untraded/incomplete months are then projected from LAST season's
    // actual for that month × this run-rate — so winter stays winter.
    let curSum = 0, priorSum = 0
    for (const cm of completeMonths) { curSum += totalNetOf(idx, category, outlet, cm); priorSum += priorIdx ? totalNetOf(priorIdx, category, outlet, cm) : 0 }
    const runRate = priorSum > 0 ? curSum / priorSum : 1

    // header
    const hr = ws.getRow(row)
    hr.getCell(1).value = outlet
    BUCKETS.forEach((b, i) => { hr.getCell(2 + i * 2).value = b; hr.getCell(3 + i * 2).value = 'TXN' })
    hr.getCell(V).value = 'Total £'; hr.getCell(W).value = 'Total TXN'; hr.getCell(X).value = 'Days'
    hr.getCell(Y).value = 'Payroll'; hr.getCell(Z).value = 'Cost of Goods'; hr.getCell(AA).value = 'Utilities'
    hr.getCell(AB).value = 'Total'; hr.getCell(AC).value = 'Without Payroll'; hr.getCell(AD).value = 'COGS %'
    hr.eachCell((c) => { c.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 9 }; fill(c, GREEN2); c.alignment = { horizontal: 'center', wrapText: true }; box(c) })
    hr.getCell(1).alignment = { horizontal: 'left' }; hr.height = 26; row++

    const monthFirst = row
    for (let m = 0; m < 12; m++) {
      const r = ws.getRow(row)
      r.getCell(1).value = MONTHS[m]
      const complete = isComplete(fy, m)
      const showHourly = traded(m) // show the real hourly split whenever the outlet took money
      const forecastTotal = forecastMode && !complete && !!priorIdx && priorTraded(m)
      // hourly £ + TXN
      BUCKETS.forEach((b, i) => {
        const rec = showHourly ? idx.hourly.get(`${category}|${outlet}|${m}|${b}`) : undefined
        const net = rec ? Math.round(rec.net) : 0
        const txn = rec ? rec.txn : 0
        const cNet = r.getCell(2 + i * 2); cNet.value = net || null; cNet.numFmt = INT; box(cNet)
        const cTxn = r.getCell(3 + i * 2); cTxn.value = txn || null; cTxn.numFmt = INT; cTxn.font = { color: { argb: 'FF999999' }, size: 9 }; box(cTxn)
      })
      const netCols = SLOT_COLS.map((c) => `${colLetter(c)}${row}`).join('+')
      const txnCols = [3, 5, 7, 9, 11, 13, 15, 17, 19, 21].map((c) => `${colLetter(c)}${row}`).join('+')
      const cV = r.getCell(V)
      if (forecastTotal) {
        // follow last season's sales curve: its actual for this month × run-rate
        cV.value = Math.round(totalNetOf(priorIdx!, category, outlet, m) * runRate)
        fill(cV, FCAST_BG)
      } else {
        cV.value = { formula: netCols }
      }
      cV.numFmt = MONEY; cV.font = { bold: true }; box(cV)
      const cW = r.getCell(W); cW.value = { formula: txnCols }; cW.numFmt = INT; box(cW)
      const cX = r.getCell(X); cX.value = idx.days.get(`${category}|${outlet}|${m}`) ?? 0; cX.numFmt = INT; box(cX)
      const cY = r.getCell(Y); const lref = labourRefs?.cell(category, outlet, m); cY.value = lref ? { formula: lref } : 0; cY.numFmt = MONEY; box(cY)
      const cZ = r.getCell(Z); const zref = cogsRefs?.cell(category, outlet, m); cZ.value = zref ? { formula: zref } : 0; cZ.numFmt = MONEY; box(cZ)
      const cAA = r.getCell(AA); cAA.value = { formula: `${colLetter(V)}${row}*${utilPct}/100` }; cAA.numFmt = MONEY; box(cAA)
      const cAB = r.getCell(AB); cAB.value = { formula: `${colLetter(V)}${row}-${colLetter(Y)}${row}-${colLetter(Z)}${row}-${colLetter(AA)}${row}` }; cAB.numFmt = MONEY; cAB.font = { bold: true }; box(cAB)
      const cAC = r.getCell(AC); cAC.value = { formula: `${colLetter(V)}${row}-${colLetter(Z)}${row}-${colLetter(AA)}${row}` }; cAC.numFmt = MONEY; box(cAC)
      const cAD = r.getCell(AD); cAD.value = { formula: `IFERROR(${colLetter(Z)}${row}/${colLetter(V)}${row},0)` }; cAD.numFmt = PCT; box(cAD)
      row++
    }
    // Total row
    const tr = row
    const trRow = ws.getRow(row); trRow.getCell(1).value = 'Total'
    for (let c = 2; c <= AD; c++) {
      if (c === AD) { const cell = trRow.getCell(c); cell.value = { formula: `IFERROR(${colLetter(Z)}${row}/${colLetter(V)}${row},0)` }; cell.numFmt = PCT; cell.font = { bold: true }; fill(cell, TOTAL_BG); box(cell); continue }
      const cell = trRow.getCell(c)
      cell.value = { formula: `SUM(${colLetter(c)}${monthFirst}:${colLetter(c)}${row - 1})` }
      cell.numFmt = (c === W || c === X) ? INT : MONEY; cell.font = { bold: true }; fill(cell, TOTAL_BG); box(cell)
    }
    trRow.getCell(1).font = { bold: true }; fill(trRow.getCell(1), TOTAL_BG); box(trRow.getCell(1))
    totalNetCell[`${category}|${outlet}`] = { row }; row += 2

    // ---- Hourly Breakdown (per hour slot, averaged over the season) ----
    const oc = outletConst(category, outlet)
    const daysRef = `$${colLetter(X)}$${tr}`
    const hbTitle = ws.getRow(row)
    hbTitle.getCell(1).value = 'Hourly Breakdown'; hbTitle.getCell(1).font = { bold: true, color: { argb: GREEN } }
    // assumption inputs: COGS %, Hours/day, Staff, £/hr
    hbTitle.getCell(V).value = 'COGS %'
    const cogsCell = hbTitle.getCell(W)
    if (opts.cogsMode === 'spreadsheet') cogsCell.value = oc.cogs / 100
    else cogsCell.value = { formula: `$${colLetter(AD)}$${tr}` }
    cogsCell.numFmt = PCT
    hbTitle.getCell(X).value = 'Hrs/day'; hbTitle.getCell(Y).value = oc.hours; hbTitle.getCell(Y).numFmt = '0.0'
    hbTitle.getCell(Z).value = 'Staff'; hbTitle.getCell(AA).value = oc.staff; hbTitle.getCell(AA).numFmt = '0'
    hbTitle.getCell(AB).value = '£/hr'; hbTitle.getCell(AC).value = { formula: `${LABOUR_RATE}` }; hbTitle.getCell(AC).numFmt = MONEY2
    for (const c of [V, X, Z, AB]) hbTitle.getCell(c).font = { italic: true, size: 9, color: { argb: 'FF6B6B6B' } }
    for (const c of [W, Y, AA, AC]) { const cc = hbTitle.getCell(c); fill(cc, FCAST_BG); box(cc) }
    const cogsRef = `$${colLetter(W)}$${row}`, hoursRef = `$${colLetter(Y)}$${row}`, staffRef = `$${colLetter(AA)}$${row}`, rateRef = `$${colLetter(AC)}$${row}`
    row++
    const m2 = MONEY2
    const incomeRow = row
    let r2 = ws.getRow(row); r2.getCell(1).value = 'Income by Hour'
    SLOT_COLS.forEach((sc) => { const c = r2.getCell(sc); c.value = { formula: `IFERROR(${colLetter(sc)}${tr}/${daysRef},0)` }; c.numFmt = m2; box(c) }); row++
    const cogsRow = row
    r2 = ws.getRow(row); r2.getCell(1).value = 'Costs of Goods'
    SLOT_COLS.forEach((sc) => { const c = r2.getCell(sc); c.value = { formula: `${colLetter(sc)}${incomeRow}*${cogsRef}` }; c.numFmt = m2; box(c) }); row++
    const grossRow = row
    r2 = ws.getRow(row)
    SLOT_COLS.forEach((sc) => { const c = r2.getCell(sc); c.value = { formula: `${colLetter(sc)}${incomeRow}-${colLetter(sc)}${cogsRow}` }; c.numFmt = m2; box(c) })
    { const a = r2.getCell(V); a.value = { formula: `AVERAGE(${AVG_COLS.map((c) => `${colLetter(c)}${row}`).join(',')})` }; a.numFmt = m2; a.font = { bold: true }; box(a) }; row++
    const utilRow = row
    r2 = ws.getRow(row); r2.getCell(1).value = 'Utilities'
    SLOT_COLS.forEach((sc) => { const c = r2.getCell(sc); c.value = { formula: `IFERROR(${colLetter(sc)}${tr}*${utilPct}/100/${daysRef}/${hoursRef},0)` }; c.numFmt = m2; box(c) }); row++
    const labourRow = row
    r2 = ws.getRow(row); r2.getCell(1).value = 'Labour'
    SLOT_COLS.forEach((sc) => { const c = r2.getCell(sc); c.value = { formula: `${staffRef}*${rateRef}` }; c.numFmt = m2; box(c) }); row++
    const costRow = row
    r2 = ws.getRow(row)
    SLOT_COLS.forEach((sc) => { const c = r2.getCell(sc); c.value = { formula: `${colLetter(sc)}${utilRow}+${colLetter(sc)}${labourRow}` }; c.numFmt = m2; box(c) })
    { const a = r2.getCell(V); a.value = { formula: `AVERAGE(${AVG_COLS.map((c) => `${colLetter(c)}${row}`).join(',')})` }; a.numFmt = m2; a.font = { bold: true }; box(a) }; row++
    r2 = ws.getRow(row); r2.getCell(1).value = 'Total'; r2.getCell(1).font = { bold: true }
    SLOT_COLS.forEach((sc) => { const c = r2.getCell(sc); c.value = { formula: `${colLetter(sc)}${grossRow}-${colLetter(sc)}${costRow}` }; c.numFmt = m2; c.font = { bold: true }; box(c) })
    { const a = r2.getCell(V); a.value = { formula: `AVERAGE(${AVG_COLS.map((c) => `${colLetter(c)}${row}`).join(',')})` }; a.numFmt = m2; a.font = { bold: true }; box(a) }
    row += 2
  }

  return { totalNet: (cat, outlet) => { const e = totalNetCell[`${cat}|${outlet}`]; return e ? ref(sheetName, V, e.row, true) : null } }
}

// ---------------------------------------------------------------------------
//  Management Accounts P&L reconciliation
// ---------------------------------------------------------------------------
function buildReconSheet(
  wb: ExcelJS.Workbook, idx: SeasonIdx, payload: RtPayload, opts: RtOptions,
  seasonLabel: string, retailGrid: GridRefs, teaGrid: GridRefs,
) {
  const ws = wb.addWorksheet('Management Accounts P&L', { views: [{ state: 'frozen', xSplit: 1, ySplit: 0 }] })
  ws.getColumn(1).width = 16
  for (let c = 2; c <= 4; c++) ws.getColumn(c).width = 14
  const t = ws.getCell(1, 1); t.value = `Management Accounts vs modelled takings · ${seasonLabel}`
  ws.mergeCells(1, 1, 1, 4); t.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } }; fill(t, GREEN); t.alignment = { vertical: 'middle' }
  ws.getRow(1).height = 22
  let row = 3
  for (const category of ['Retail', 'Tea Room'] as const) {
    const outlets = baseOutlets(payload, category, opts)
    if (!outlets.length) continue
    const grid = category === 'Retail' ? retailGrid : teaGrid
    const sec = ws.getCell(row, 1); sec.value = category === 'Retail' ? 'RETAIL' : 'CATERING'
    ws.mergeCells(row, 1, row, 4); sec.font = { bold: true, color: { argb: GREEN } }; fill(sec, SUBHEAD); row++
    const hdr = ws.getRow(row)
    hdr.getCell(1).value = 'Outlet'; hdr.getCell(2).value = 'Mgmt Accts'; hdr.getCell(3).value = 'Modelled (till)'; hdr.getCell(4).value = 'Variance'
    for (const c of [1, 2, 3, 4]) { const cc = hdr.getCell(c); cc.font = { bold: true, color: { argb: 'FFFFFFFF' } }; fill(cc, GREEN2); cc.alignment = { horizontal: c === 1 ? 'left' : 'right' }; box(cc) }
    row++
    const first = row
    for (const outlet of outlets) {
      const r = ws.getRow(row); r.getCell(1).value = outlet
      let maTot = 0; for (let m = 0; m < 12; m++) maTot += idx.ma.get(`${category}|${outlet}|${m}`) ?? 0
      const cMa = r.getCell(2); cMa.value = Math.round(maTot); cMa.numFmt = MONEY2; box(cMa)
      const gref = grid.totalNet(category, outlet)
      const cMod = r.getCell(3)
      if (gref) cMod.value = { formula: gref }
      else { let till = 0; for (let m = 0; m < 12; m++) till += totalNetOf(idx, category, outlet, m); cMod.value = Math.round(till) }
      cMod.numFmt = MONEY2; box(cMod)
      const cVar = r.getCell(4); cVar.value = { formula: `B${row}-C${row}` }; cVar.numFmt = MONEY2; box(cVar)
      box(r.getCell(1)); row++
    }
    const trr = ws.getRow(row); trr.getCell(1).value = 'Total'
    for (const c of [2, 3, 4]) { const cell = trr.getCell(c); cell.value = { formula: `SUM(${colLetter(c)}${first}:${colLetter(c)}${row - 1})` }; cell.numFmt = MONEY2; cell.font = { bold: true }; fill(cell, TOTAL_BG); box(cell) }
    trr.getCell(1).font = { bold: true }; fill(trr.getCell(1), TOTAL_BG); box(trr.getCell(1)); row += 2
  }
  const note = ws.getCell(row, 1)
  note.value = 'Mgmt Accts = iplicit management-accounts income by outlet (P&L). Modelled = the season’s EPOS till takings from the by-hour grid. Variance = Mgmt Accts − Modelled.'
  ws.mergeCells(row, 1, row, 4); note.font = { italic: true, size: 9, color: { argb: 'FF6B6B6B' } }; note.alignment = { wrapText: true }
}

// ---------------------------------------------------------------------------
//  Assemble the workbook
// ---------------------------------------------------------------------------
function buildSeasonSet(
  wb: ExcelJS.Workbook, seasonIdx: SeasonIdx, priorIdx: SeasonIdx | null,
  payload: RtPayload, opts: RtOptions, seasonLabel: string, utilPct: number,
  primary: boolean,
) {
  const suffix = primary ? '' : ` ${seasonLabel.replace('/', '-')}`
  const cogsRefs = buildSplitSheet(wb, `Costs of Goods${suffix}`, 'COGS', seasonIdx, payload, opts, `Costs of Goods · ${seasonLabel}`)
  const retLab = seasonIdx.hasLabour ? buildSplitSheet(wb, `Retail Labour${suffix}`, 'Labour', seasonIdx, payload, opts, `Retail Labour · ${seasonLabel}`, 'Retail') : null
  const catLab = seasonIdx.hasLabour ? buildSplitSheet(wb, `Catering Labour${suffix}`, 'Labour', seasonIdx, payload, opts, `Catering Labour · ${seasonLabel}`, 'Tea Room') : null
  const labourRefsFor = (cat: 'Retail' | 'Tea Room') => (cat === 'Retail' ? retLab : catLab)
  // Grid sheets need one SplitRefs for labour, so wrap the two.
  const labourRefs: SplitRefs = { cell: (cat, outlet, month) => labourRefsFor(cat as 'Retail' | 'Tea Room')?.cell(cat, outlet, month) ?? null }

  const retailGrid = buildGridSheet(wb, `Retail${suffix}`, 'Retail', seasonIdx, payload, opts, seasonLabel, cogsRefs, labourRefs, utilPct)
  const teaGrid = buildGridSheet(wb, `Tea Rooms${suffix}`, 'Tea Room', seasonIdx, payload, opts, seasonLabel, cogsRefs, labourRefs, utilPct)

  if (primary) {
    buildReconSheet(wb, seasonIdx, payload, opts, seasonLabel, retailGrid, teaGrid)
    // with-Forecast grids (their own copies, forecast months filled)
    buildGridSheet(wb, 'Retail with Forecast', 'Retail', seasonIdx, payload, opts, seasonLabel, cogsRefs, labourRefs, utilPct, true, priorIdx)
    buildGridSheet(wb, 'Tea Rooms with Forecast', 'Tea Room', seasonIdx, payload, opts, seasonLabel, cogsRefs, labourRefs, utilPct, true, priorIdx)
  }
}

export async function buildRetailTearoomWorkbook(payload: RtPayload, opts: RtOptions): Promise<ExcelJS.Workbook> {
  const ExcelJSlib = (await import('exceljs')).default
  const wb = new ExcelJSlib.Workbook()
  wb.creator = 'NYMR Reports'; wb.created = new Date()
  const utilPct = payload.model?.utilities_pct ?? 5

  const primaryIdx = indexSeason(payload.seasons[String(payload.fiscalYear)])
  const prevIdx = payload.seasons[String(payload.fiscalYear - 1)] ? indexSeason(payload.seasons[String(payload.fiscalYear - 1)]) : null
  buildSeasonSet(wb, primaryIdx, prevIdx, payload, opts, payload.fiscalYearLabel, utilPct, true)

  if (opts.comparisonFy && payload.seasons[String(opts.comparisonFy)]) {
    const compIdx = indexSeason(payload.seasons[String(opts.comparisonFy)])
    const compLabel = payload.seasons[String(opts.comparisonFy)].fiscalYearLabel
    buildSeasonSet(wb, compIdx, null, payload, opts, compLabel, utilPct, false)
  }
  return wb
}

export async function downloadRetailTearoomXlsx(payload: RtPayload, opts: RtOptions): Promise<void> {
  const wb = await buildRetailTearoomWorkbook(payload, opts)
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `NYMR-Retail-TeaRoom-Analysis-${payload.fiscalYearLabel.replace('/', '-')}.xlsx`
  document.body.appendChild(a); a.click(); document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}
